/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  DeleteAdminV1StoresStoreIdWebhooksIdPathParameters,
  GetAdminV1StoresStoreIdWebhooks200,
  GetAdminV1StoresStoreIdWebhooksEvents200,
  GetAdminV1StoresStoreIdWebhooksEventsPathParameters,
  GetAdminV1StoresStoreIdWebhooksIdDeliveries200,
  GetAdminV1StoresStoreIdWebhooksIdDeliveriesParams,
  GetAdminV1StoresStoreIdWebhooksIdDeliveriesPathParameters,
  GetAdminV1StoresStoreIdWebhooksIdPathParameters,
  GetAdminV1StoresStoreIdWebhooksPathParameters,
  PatchAdminV1StoresStoreIdWebhooksIdBody,
  PatchAdminV1StoresStoreIdWebhooksIdPathParameters,
  PostAdminV1StoresStoreIdWebhooksBody,
  PostAdminV1StoresStoreIdWebhooksIdDeliveriesDeliveryIdRedeliverPathParameters,
  PostAdminV1StoresStoreIdWebhooksIdRotateSecretPathParameters,
  PostAdminV1StoresStoreIdWebhooksIdTestPathParameters,
  PostAdminV1StoresStoreIdWebhooksPathParameters,
  WebhookDelivery,
  WebhookEndpoint,
  WebhookEndpointWithSecret
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Event names an endpoint can subscribe to
 */
export const getAdminV1StoresStoreIdWebhooksEvents = (
    { storeId }: GetAdminV1StoresStoreIdWebhooksEventsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdWebhooksEvents200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdWebhooksEvents200>(
      {url: `/admin/v1/stores/${storeId}/webhooks/events`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Webhook endpoints of the store
 */
export const getAdminV1StoresStoreIdWebhooks = (
    { storeId }: GetAdminV1StoresStoreIdWebhooksPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdWebhooks200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdWebhooks200>(
      {url: `/admin/v1/stores/${storeId}/webhooks`, method: 'GET'
    },
      options);
    }
  /**
 * The signing secret is in this response only. Deliveries carry X-Nucleus-Signature `t=<unix>,v1=<hex HMAC-SHA256 of "<t>.<body>">`, X-Nucleus-Event and X-Nucleus-Delivery-Id; six attempts with exponential backoff, then the delivery is failed and the endpoint is disabled after 20 consecutive failures
 * @summary Register an endpoint
 */
export const postAdminV1StoresStoreIdWebhooks = (
    { storeId }: PostAdminV1StoresStoreIdWebhooksPathParameters,
    postAdminV1StoresStoreIdWebhooksBody: BodyType<PostAdminV1StoresStoreIdWebhooksBody>,
 options?: SecondParameter<typeof customInstance<WebhookEndpointWithSecret>>,) => {
      return customInstance<WebhookEndpointWithSecret>(
      {url: `/admin/v1/stores/${storeId}/webhooks`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdWebhooksBody
    },
      options);
    }
  /**
 * @summary One endpoint
 */
export const getAdminV1StoresStoreIdWebhooksId = (
    { storeId, id }: GetAdminV1StoresStoreIdWebhooksIdPathParameters,
 options?: SecondParameter<typeof customInstance<WebhookEndpoint>>,) => {
      return customInstance<WebhookEndpoint>(
      {url: `/admin/v1/stores/${storeId}/webhooks/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Change url, events, description or switch the endpoint on and off
 */
export const patchAdminV1StoresStoreIdWebhooksId = (
    { storeId, id }: PatchAdminV1StoresStoreIdWebhooksIdPathParameters,
    patchAdminV1StoresStoreIdWebhooksIdBody: BodyType<PatchAdminV1StoresStoreIdWebhooksIdBody>,
 options?: SecondParameter<typeof customInstance<WebhookEndpoint>>,) => {
      return customInstance<WebhookEndpoint>(
      {url: `/admin/v1/stores/${storeId}/webhooks/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdWebhooksIdBody
    },
      options);
    }
  /**
 * @summary Remove an endpoint and its delivery log
 */
export const deleteAdminV1StoresStoreIdWebhooksId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdWebhooksIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/webhooks/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Deliveries sign with the new secret from now on; the old one stops working at once
 * @summary Issue a new signing secret
 */
export const postAdminV1StoresStoreIdWebhooksIdRotateSecret = (
    { storeId, id }: PostAdminV1StoresStoreIdWebhooksIdRotateSecretPathParameters,
 options?: SecondParameter<typeof customInstance<WebhookEndpointWithSecret>>,) => {
      return customInstance<WebhookEndpointWithSecret>(
      {url: `/admin/v1/stores/${storeId}/webhooks/${id}/rotate-secret`, method: 'POST'
    },
      options);
    }
  /**
 * @summary Queue a webhook.test delivery
 */
export const postAdminV1StoresStoreIdWebhooksIdTest = (
    { storeId, id }: PostAdminV1StoresStoreIdWebhooksIdTestPathParameters,
 options?: SecondParameter<typeof customInstance<WebhookDelivery>>,) => {
      return customInstance<WebhookDelivery>(
      {url: `/admin/v1/stores/${storeId}/webhooks/${id}/test`, method: 'POST'
    },
      options);
    }
  /**
 * @summary Delivery log of the endpoint, newest first
 */
export const getAdminV1StoresStoreIdWebhooksIdDeliveries = (
    { storeId, id }: GetAdminV1StoresStoreIdWebhooksIdDeliveriesPathParameters,
    params?: GetAdminV1StoresStoreIdWebhooksIdDeliveriesParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdWebhooksIdDeliveries200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdWebhooksIdDeliveries200>(
      {url: `/admin/v1/stores/${storeId}/webhooks/${id}/deliveries`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Send a past delivery again as a new delivery
 */
export const postAdminV1StoresStoreIdWebhooksIdDeliveriesDeliveryIdRedeliver = (
    { storeId, id, deliveryId }: PostAdminV1StoresStoreIdWebhooksIdDeliveriesDeliveryIdRedeliverPathParameters,
 options?: SecondParameter<typeof customInstance<WebhookDelivery>>,) => {
      return customInstance<WebhookDelivery>(
      {url: `/admin/v1/stores/${storeId}/webhooks/${id}/deliveries/${deliveryId}/redeliver`, method: 'POST'
    },
      options);
    }
  export type GetAdminV1StoresStoreIdWebhooksEventsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdWebhooksEvents>>>
export type GetAdminV1StoresStoreIdWebhooksResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdWebhooks>>>
export type PostAdminV1StoresStoreIdWebhooksResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdWebhooks>>>
export type GetAdminV1StoresStoreIdWebhooksIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdWebhooksId>>>
export type PatchAdminV1StoresStoreIdWebhooksIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdWebhooksId>>>
export type DeleteAdminV1StoresStoreIdWebhooksIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdWebhooksId>>>
export type PostAdminV1StoresStoreIdWebhooksIdRotateSecretResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdWebhooksIdRotateSecret>>>
export type PostAdminV1StoresStoreIdWebhooksIdTestResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdWebhooksIdTest>>>
export type GetAdminV1StoresStoreIdWebhooksIdDeliveriesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdWebhooksIdDeliveries>>>
export type PostAdminV1StoresStoreIdWebhooksIdDeliveriesDeliveryIdRedeliverResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdWebhooksIdDeliveriesDeliveryIdRedeliver>>>
